import { useMemo } from 'react';

import {
	EKHO_ONLY_SECTIONS,
	ONBOARDING_CODE_TO_SERVICE_ACCESS_TEMPLATE,
	OnboardingCode,
	type Section,
	SERVICE_ACCESS_SERVICES_TEMPLATES,
	type ServiceAccessObject,
	type ServiceAccessTemplateCode,
	type Subsection,
	SUBSECTIONS,
} from '@ekhodealer/ekho-common';

import { useCurrentServicesProvider } from '@common/hooks/useCurrentServicesProvider';

import { getCombinedServiceSections } from '../lib/serviceFormConfig/formConfigs/utils/serviceAccess';

/**
 * Helper to get service access template from onboarding code
 * This is a fallback for backwards compatibility during migration
 */
function getTemplateFromOnboardingCode(
	onboardingCode: string | undefined,
): ServiceAccessTemplateCode | undefined {
	if (!onboardingCode) return undefined;

	return onboardingCode in OnboardingCode
		? ONBOARDING_CODE_TO_SERVICE_ACCESS_TEMPLATE[onboardingCode as OnboardingCode]
		: undefined;
}

/**
 * Subsections that are Ekho-only and should not be shown to users.
 * Future: AuthZ will control visibility instead of hardcoded filtering.
 */
const EKHO_ONLY_SUBSECTIONS: Subsection[] = [
	SUBSECTIONS.CHECKOUT_ASSIGNMENT,
	// Add other Ekho-only subsections here as needed
];

/**
 * Hook to get all combined service sections for the current services provider,
 * filtered to exclude Ekho-only sections.
 * Future: AuthZ will control visibility instead of hardcoded filtering.
 * @returns Array of sections that should be visible in the nav menu
 */
export function useCombinedServiceSections(): Section[] {
	const { data: servicesProvider } = useCurrentServicesProvider();

	return useMemo(() => {
		// Get serviceAccess, or derive from onboardingCode for backwards compatibility
		let serviceAccess = servicesProvider?.serviceAccess;

		if (!serviceAccess && servicesProvider?.onboardingCode) {
			const templateCode = getTemplateFromOnboardingCode(servicesProvider.onboardingCode);
			if (templateCode) {
				const templateConfig = SERVICE_ACCESS_SERVICES_TEMPLATES[templateCode];
				if (templateConfig) {
					serviceAccess = {
						required: templateConfig.required,
						optional: templateConfig.optional,
						template: templateCode,
					};
				}
			}
		}

		if (!serviceAccess) {
			return [];
		}

		const allSections = getCombinedServiceSections(serviceAccess);
		// Filter out Ekho-only sections
		// Future: AuthZ will control visibility instead of hardcoded filtering
		const ekhoOnlySet = new Set<Section>(EKHO_ONLY_SECTIONS);
		return allSections.filter((section) => !ekhoOnlySet.has(section));
	}, [servicesProvider]);
}

/**
 * Hook to get program-specific information about a section and optionally a subsection
 * @param section - The section to check
 * @param subsection - Optional subsection to check (if provided, will also check if subsection is Ekho-only)
 * @returns Object containing program information about the section/subsection
 */
export function useServiceProgramInfo(section: Section, subsection?: Subsection) {
	const { data: servicesProvider } = useCurrentServicesProvider();

	return useMemo(() => {
		// If no servicesProvider, section is not in program
		if (!servicesProvider) {
			return {
				isInProgram: false,
				isRequired: false,
				isOptional: false,
				isEnabled: false,
				shouldRender: false,
			};
		}

		// Get program services configuration from serviceAccess
		// If no serviceAccess, try to derive from onboardingCode for backwards compatibility
		let serviceAccess: ServiceAccessObject | undefined = servicesProvider.serviceAccess;

		if (!serviceAccess && servicesProvider.onboardingCode) {
			const templateCode = getTemplateFromOnboardingCode(servicesProvider.onboardingCode);
			if (templateCode) {
				// Create a serviceAccess object from the template
				const templateConfig = SERVICE_ACCESS_SERVICES_TEMPLATES[templateCode];
				if (templateConfig) {
					serviceAccess = {
						required: templateConfig.required,
						optional: templateConfig.optional,
						template: templateCode,
					};
				}
			}
		}

		if (!serviceAccess) {
			return {
				isInProgram: false,
				isRequired: false,
				isOptional: false,
				isEnabled: false,
				shouldRender: false,
			};
		}

		// Check if subsection is Ekho-only (if provided)
		if (subsection) {
			if (EKHO_ONLY_SUBSECTIONS.includes(subsection)) {
				return {
					isInProgram: false,
					isRequired: false,
					isOptional: false,
					isEnabled: false,
					shouldRender: false,
				};
			}
		}

		// Get combined sections from template and direct serviceAccess
		const validSections = getCombinedServiceSections(serviceAccess);
		const ekhoOnlySet = new Set<Section>(EKHO_ONLY_SECTIONS);
		const isInProgram = validSections.includes(section) && !ekhoOnlySet.has(section);

		// If not in program, don't render
		if (!isInProgram) {
			return {
				isInProgram: false,
				isRequired: false,
				isOptional: false,
				isEnabled: false,
				shouldRender: false,
			};
		}

		// Determine if section is required or optional
		// Check both template and direct serviceAccess
		const {
			required: directRequired = [],
			optional: directOptional = [],
			template,
		} = serviceAccess;

		let templateRequired: Section[] = [];
		let templateOptional: Section[] = [];

		if (template) {
			const templateConfig =
				SERVICE_ACCESS_SERVICES_TEMPLATES[template as ServiceAccessTemplateCode];
			if (templateConfig) {
				templateRequired = templateConfig.required;
				templateOptional = templateConfig.optional;
			}
		}

		const isRequired = templateRequired.includes(section) || directRequired.includes(section);
		const isOptional = templateOptional.includes(section) || directOptional.includes(section);

		// Section should render only if:
		// 1. It's in program (in combined sections array)
		// 2. It's not an Ekho-only section
		// 3. If subsection is provided, it's not an Ekho-only subsection
		const shouldRender = isInProgram;

		// Enabled state defaults to true for required sections
		// The actual enabled/disabled state is managed by the form config template
		const isEnabled = isRequired;

		return {
			isInProgram: true,
			isRequired,
			isOptional,
			isEnabled,
			shouldRender,
		};
	}, [servicesProvider, section, subsection]);
}
